import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectClient, selectSession, selectSessionAdvisor } from '@client/model';
import { SessionType } from '@shared';
import { useSession, useSessionMutations } from '@/features/guided-financial-planning/context';
import { buildPolicyFundingSource } from '@/features/guided-financial-planning/utils';
import {
  buildCcrcTypeAPendingPolicy,
  type CcrcCoverageType,
  type CcrcTypeAPricingBreakdown,
} from '@/features/ccrc-type-a';
import { getQuoteId, type GuidedPlanningQuote } from './use-quotes-display';
import { getFundingAllocationActions } from '@/features/guided-financial-planning/stores';

interface SelectedQuoteInfo {
  quoteId: string;
  quote?: GuidedPlanningQuote;
  ccrcTypeAProduct?: CcrcTypeAGuidedSelection;
  ccrcCouplePolicies?: CcrcTypeACouplePolicies;
  policyFundingSource: PendingPolicyFundingSource;
}

interface AddCcrcTypeAProductArgs {
  product: CcrcTypeAProduct;
  coverage: CcrcCoverageType;
  pricing: CcrcTypeAPricingBreakdown;
}

interface UseAddPolicyFromQuoteResult {
  /** The first selected quote (for single-selection mode) */
  selectedQuote: SelectedQuoteInfo | null;
  /** Check if a specific quote is already added */
  isQuoteAdded: (quote: GuidedPlanningQuote) => boolean;
  /** Add a quote to the session */
  addQuote: (quote: GuidedPlanningQuote) => string | null;
  /** Remove a quote from the session */
  removeQuote: (quote: GuidedPlanningQuote) => void;
  addCcrcTypeAProduct: (args: AddCcrcTypeAProductArgs) => string | null;
  removeCcrcTypeAProduct: () => void;
}

/**
 * Hook to manage quote selection in the guided planning session.
 *
 * Stores quote data and pre-built PolicyFundingSource in session.stepData.selectedQuotes.
 * The policy is only persisted to the client when the session is finalized.
 */
export function useAddPolicyFromQuote(): UseAddPolicyFromQuoteResult {
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);
  const { session } = useSession();
  const { updateStepData } = useSessionMutations();

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const selectedQuote = useMemo((): SelectedQuoteInfo | null => {
    const selectedQuotes = (session?.stepData?.selectedQuotes ?? {}) as Record<
      string,
      SelectedQuoteData
    >;
    const entries = Object.entries(selectedQuotes);
    if (entries.length === 0) return null;

    const [quoteId, data] = entries[0];
    return {
      quoteId,
      quote: data.quote as GuidedPlanningQuote | undefined,
      ccrcTypeAProduct: data.ccrcTypeAProduct,
      ccrcCouplePolicies: data.ccrcCouplePolicies,
      policyFundingSource: data.policyFundingSource,
    };
  }, [session?.stepData?.selectedQuotes]);

  const isQuoteAdded = (quote: GuidedPlanningQuote): boolean => {
    const quoteId = getQuoteId(quote);
    const selectedQuotes = session?.stepData?.selectedQuotes ?? {};
    return quoteId in selectedQuotes;
  };

  const addQuote = (quote: GuidedPlanningQuote): string | null => {
    if (!client?.clientId) {
      console.warn('Cannot add policy: client not found');
      return null;
    }

    if (!session?.id) {
      console.warn('Cannot add policy: no active guided planning session');
      return null;
    }

    const quoteId = getQuoteId(quote);

    if (isQuoteAdded(quote)) {
      return quoteId;
    }

    const policyFundingSource = buildPolicyFundingSource({
      quote,
      sessionId: session.id,
      client,
      isAdvisorSession,
      advisorId: advisor?.advisorId,
    });

    const currentSelectedQuotes = session.stepData?.selectedQuotes ?? {};
    const currentAllocations = session.stepData?.policyFundingAllocations ?? {};

    updateStepData({
      selectedQuotes: {
        ...currentSelectedQuotes,
        [quoteId]: {
          quoteId,
          quote,
          policyFundingSource,
        },
      },
      policyFundingAllocations: {
        ...currentAllocations,
        [quoteId]: {},
      },
    });

    getFundingAllocationActions().initFromSession({
      ...currentAllocations,
      [quoteId]: {},
    });

    return quoteId;
  };

  const removeQuote = (quote: GuidedPlanningQuote): void => {
    const quoteId = getQuoteId(quote);
    const currentSelectedQuotes = { ...(session?.stepData?.selectedQuotes ?? {}) };
    const currentAllocations = { ...(session?.stepData?.policyFundingAllocations ?? {}) };

    delete currentSelectedQuotes[quoteId];
    delete currentAllocations[quoteId];

    updateStepData({
      selectedQuotes: currentSelectedQuotes,
      policyFundingAllocations: currentAllocations,
    });

    getFundingAllocationActions().initFromSession(currentAllocations);
  };

  const addCcrcTypeAProduct = (args: AddCcrcTypeAProductArgs): string | null => {
    if (!client?.clientId) {
      console.warn('Cannot add CCRC plan: client not found');

      return null;
    }
    if (!session?.id) {
      console.warn('Cannot add CCRC plan: no active guided planning session');

      return null;
    }

    const { product, coverage, pricing } = args;
    const quoteId = `ccrc-type-a-${product.id}`;
    const advisorId = isAdvisorSession ? advisor?.advisorId : undefined;

    const policyFundingSource = buildCcrcTypeAPendingPolicy({
      product,
      entranceFee: pricing.totalEntranceFee,
      monthlyFee: pricing.totalMonthlyFee,
      addedByAdvisorId: advisorId,
      guidedPlanningSessionId: session.id,
    });

    // Couple coverage carries one membership per partner so the plan
    // comparison can run couple-mode over both partners' care and coverage.
    const ccrcCouplePolicies: CcrcTypeACouplePolicies | undefined =
      coverage === 'couple' && pricing.partnerEntranceFee != null
        ? {
            primary: buildCcrcTypeAPendingPolicy({
              product,
              entranceFee: pricing.primaryEntranceFee,
              monthlyFee: pricing.monthlyPerPerson,
              addedByAdvisorId: advisorId,
              guidedPlanningSessionId: session.id,
            }),
            partner: buildCcrcTypeAPendingPolicy({
              product,
              entranceFee: pricing.partnerEntranceFee,
              monthlyFee: pricing.monthlyPerPerson,
              addedByAdvisorId: advisorId,
              guidedPlanningSessionId: session.id,
            }),
          }
        : undefined;

    const ccrcTypeAProduct: CcrcTypeAGuidedSelection = {
      productId: product.id,
      productName: product.productName,
      communityName: product.communityName,
      coverage,
      totalEntranceFee: pricing.totalEntranceFee,
      totalMonthlyFee: pricing.totalMonthlyFee,
      annualIncreaseRate: product.annualIncreaseRate,
    };

    // CCRC mode has a single selection, replace any prior selection outright.
    updateStepData({
      selectedQuotes: {
        [quoteId]: { quoteId, ccrcTypeAProduct, ccrcCouplePolicies, policyFundingSource },
      },
      policyFundingAllocations: { [quoteId]: {} },
    });

    getFundingAllocationActions().initFromSession({ [quoteId]: {} });

    return quoteId;
  };

  const removeCcrcTypeAProduct = (): void => {
    updateStepData({ selectedQuotes: {}, policyFundingAllocations: {} });
    getFundingAllocationActions().initFromSession({});
  };

  return {
    selectedQuote,
    isQuoteAdded,
    addQuote,
    removeQuote,
    addCcrcTypeAProduct,
    removeCcrcTypeAProduct,
  };
}
